import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getSeparatedWasteData } from '../services/wasteService';
import { IosArrowRight } from '../assets/icons';
import { formatDate } from '../helpers/stringHelpers';

const PHOTO_BASE_URL = process.env.PHOTO_BASE_URL || 'https://apiizdonusum.izmirteknoloji.com.tr/YuklenenDosyalar';

const HouseDetail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [houseData, setHouseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRejectedWaste, setSelectedRejectedWaste] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    const fetchHouseData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const address = searchParams.get('adres');
        if (!address) {
          setError('Adres bilgisi bulunamadı.');
          return;
        }

        const response = await getSeparatedWasteData({
          address: address
        });

        if (response.status === 200 && response.data && response.data.length > 0) {
          setHouseData(response.data[0]);
          // Select the latest rejected waste by default
          const rejectedWastes = response.data[0].records.filter(r => !r.status);
          if (rejectedWastes.length > 0) {
            const latestRejected = rejectedWastes.sort((a, b) => 
              new Date(b.dateReceived) - new Date(a.dateReceived)
            )[0];
            setSelectedRejectedWaste(latestRejected);
            // Select the first photo by default if available
            if (latestRejected.photos && latestRejected.photos.length > 0) {
              setSelectedPhoto(latestRejected.photos[0]);
            }
          }
        } else {
          setError('Bu adrese ait veri bulunamadı.');
        }
      } catch (error) {
        console.error('Failed to fetch house data:', error);
        setError('Hane detayları yüklenirken bir hata oluştu.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchHouseData();
  }, [searchParams]);

  const handleRejectedWasteClick = (waste) => {
    setSelectedRejectedWaste(waste);
    if (waste.photos && waste.photos.length > 0) {
      setSelectedPhoto(waste.photos[0]);
    } else {
      setSelectedPhoto(null);
    }
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handlePhotoView = () => {
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setIsLightboxOpen(false);
  };

  if (isLoading) {
    return (
      <div className="house-detail-container">
        <h1 className="page-title back-link" onClick={handleBack}>Ayrıştırma Verileri</h1>
        <h1 className="page-title">&gt;&nbsp; Hane Detayları</h1>
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="house-detail-container">
        <h1 className="page-title back-link" onClick={handleBack}>Ayrıştırma Verileri</h1>
        <h1 className="page-title">&gt;&nbsp; Hane Detayları</h1>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  if (!houseData) {
    return (
      <div className="house-detail-container">
        <h1 className="page-title back-link" onClick={handleBack}>Ayrıştırma Verileri</h1>
        <h1 className="page-title">&gt;&nbsp; Hane Detayları</h1>
        <div className="error-message">Veri bulunamadı</div>
      </div>
    );
  }

  return (
    <div className="house-detail-container">
      <div className="header-section">
        <h1 className="page-title back-link" onClick={handleBack}>Ayrıştırma Verileri</h1>
        <h1 className="page-title">&gt;&nbsp; Hane Detayları</h1>
      </div>

      <div className="detail-section">
        <div className="address-info">
          <h2>Adres: </h2>
          <p>{houseData.address}, <span>{houseData.records[0]?.district || '-'}</span></p>
        </div>
        
        <div className='approved-waste detail-waste-container'>
          <h2>Onaylanan Atıklar</h2>
          <div className='waste-list'>
            {houseData.records.filter(r => r.status).length > 0 ? (
              houseData.records.filter(r => r.status).map(record => (
                <div key={record.qr} className='waste-item'>{record.qr}</div>
              ))
            ) : (
              <div className="no-data-message">Onaylanan Atık Bulunamadı</div>
            )}
          </div>
        </div>

        <div className='rejected-waste detail-waste-container'>
          <h2 className='waste-title'>Reddedilen Atıklar</h2>
          <div className='waste-list'>
            {houseData.records.filter(r => !r.status).length > 0 ? (
              houseData.records.filter(r => !r.status).map(record => (
                <div 
                  key={record.qr} 
                  className={`waste-item rejected ${selectedRejectedWaste?.qr === record.qr ? 'active' : ''}`}
                  onClick={() => handleRejectedWasteClick(record)}
                >
                  {record.qr}
                </div>
              ))
            ) : (
              <div className="no-data-message">Reddedilen Atık Bulunamadı</div>
            )}
          </div>
        </div>

        {selectedRejectedWaste && selectedRejectedWaste.photos && selectedRejectedWaste.photos.length > 0 && (
          <>
            <div className='rejected-photos-container'>
              <h2 className='waste-title'>{selectedRejectedWaste.qr} Atığın Fotoğrafları</h2>
              <div className='photos-list'>
                {selectedRejectedWaste.photos.map((photo, index) => (
                  <div 
                    key={photo.id} 
                    className={`photo-item ${selectedPhoto?.id === photo.id ? 'active' : ''}`}
                    onClick={() => handlePhotoClick(photo)}
                  >
                    <img 
                      src={`${PHOTO_BASE_URL}/${photo.photo}`} 
                      alt={`Rejected waste ${selectedRejectedWaste.qr} ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            {selectedPhoto && (
              <>
                <div className='photo-description-container'>
                  <h2 className='waste-title photo-desc-title'>
                    {selectedRejectedWaste.qr} {selectedRejectedWaste.photos.findIndex(p => p.id === selectedPhoto.id) + 1} Numaralı Fotoğrafı ve Açıklaması
                    <span className="date-received">{formatDate(selectedRejectedWaste.dateReceived)}</span>
                  </h2>
                  <p className='photo-description'>{selectedPhoto.description}</p>
                </div>
                <button className='photo-desc-btn' onClick={handlePhotoView}>
                  <p>Fotoğrafı İncele</p>
                  <IosArrowRight color='#C70000' />
                </button>
              </>
            )}
          </>
        )}
      </div>

      {isLightboxOpen && selectedPhoto && (
        <div className="lightbox-backdrop" onClick={handleCloseLightbox}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <img 
              src={`${PHOTO_BASE_URL}/${selectedPhoto.photo}`} 
              alt={`Rejected waste ${selectedRejectedWaste.qr}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default HouseDetail; 